import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  Filter,
  List,
  ArrowRight
} from 'lucide-react';
import { POrderLineDto } from '../../../../../types/porders';
import { stcTransService, StcTransDto } from '../../../../../services/stcTransService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface POrderLineBrowserProps {
  orderNo: number;
  orderLines: POrderLineDto[];
  onLineSelect: (line: POrderLineDto) => void;
  onAllLinesSelect?: (lines: POrderLineDto[]) => void; // Ny callback for bulk-operasjon
  onClose: () => void;
  batchId?: string; // Valgfri: For bilmottak/forsendelse der batchId allerede er kjent
}

interface LineWithArrived extends POrderLineDto {
  arrivedQuantity: number; // Ankommet (sum av StcTrans.Quantity der LineStatus = 20)
  receivedQuantity: number; // Mottatt (sum av StcTrans.Quantity der LineStatus > 20)
}

export const POrderLineBrowser: React.FC<POrderLineBrowserProps> = ({
  orderNo,
  orderLines,
  onLineSelect,
  onAllLinesSelect,
  onClose,
  batchId: externalBatchId
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [filteredLines, setFilteredLines] = useState<LineWithArrived[]>([]);
  const [linesWithArrived, setLinesWithArrived] = useState<LineWithArrived[]>([]);

  // Funksjon for å velge alle filtrerte linjer
  const handleSelectAllLines = () => {
    if (onAllLinesSelect) {
      // Hvis bulk-callback er definert, bruk den
      onAllLinesSelect(filteredLines);
    } else {
      // Fallback til å kalle onLineSelect for hver linje
      filteredLines.forEach(line => {
        onLineSelect(line);
      });
    }
  };

  // Hent StcTrans og beregn "Ankommet" for hver linje
  useEffect(() => {
    const loadArrivedQuantities = async () => {
      try {
        let allTransactions: StcTransDto[];

        if (externalBatchId) {
          // Bilmottak/forsendelse: batchId er allerede kjent, hent transaksjoner direkte
          allTransactions = await stcTransService.getTransactionsByBatch(externalBatchId);
        } else {
          // Enkeltordre (Modus 1): Hent BatchID basert på orderNo
          const batchId = await stcTransService.getOrCreateBatchId(orderNo);
          allTransactions = await stcTransService.getTransactionsByBatch(batchId);
        }

        // Beregn ankommet og mottatt antall per linje
        const linesWithArrivedData: LineWithArrived[] = orderLines.map(line => {
          // Filtrer StcTrans for denne linjen - bruk linjens egen orderNo (viktig for multi-ordre)
          const lineOrderNo = line.orderNo ?? orderNo;
          const lineTransactions = allTransactions.filter(
            (t: StcTransDto) => t.orderNo === lineOrderNo && t.lineNo === line.lineNo
          );
          // Ankommet: sum av StcTrans.Quantity der LineStatus = 20 (UnderArbeid)
          const arrivedQty = lineTransactions
            .filter((t: StcTransDto) => t.lineStatus === 20)
            .reduce(
              (sum: number, t: StcTransDto) => sum + (t.quantity || 0),
              0
            );
          // Mottatt: sum av StcTrans.Quantity der LineStatus > 20 (bekreftet/fullført)
          const receivedQty = lineTransactions
            .filter((t: StcTransDto) => t.lineStatus > 20)
            .reduce(
              (sum: number, t: StcTransDto) => sum + (t.quantity || 0),
              0
            );

          return {
            ...line,
            arrivedQuantity: arrivedQty,
            receivedQuantity: receivedQty
          };
        });

        // Filtrer bort ferdigmottatte linjer (kun vis linjer det er aktuelt å jobbe med)
        const activeLines = linesWithArrivedData.filter(line => {
          const bestilt = line.quantity || 0;
          if (bestilt === 0) return false; // Ingen bestilt mengde – ikke relevant
          const totalBehandlet = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
          return totalBehandlet < bestilt; // Vis kun linjer som ikke er fullt behandlet
        });

        setLinesWithArrived(activeLines);
      } catch (error) {
        console.error('Kunne ikke hente StcTrans:', error);
        // Fallback: sett arrivedQuantity og receivedQuantity til 0
        const linesWithZero: LineWithArrived[] = orderLines.map(line => ({
          ...line,
          arrivedQuantity: 0,
          receivedQuantity: 0
        }));
        setLinesWithArrived(linesWithZero);
      }
    };

    loadArrivedQuantities();
  }, [orderNo, orderLines, externalBatchId]);

  // Filtrer linjer basert på søketerm
  useEffect(() => {
    if (!searchValue) {
      setFilteredLines(linesWithArrived);
      return;
    }

    const filtered = linesWithArrived.filter(line =>
      line.itemNo.toLowerCase().includes(searchValue.toLowerCase()) ||
      (line.description1 || '').toLowerCase().includes(searchValue.toLowerCase()) ||
      (line.itemBarCode || '').toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredLines(filtered);
  }, [searchValue, linesWithArrived]);

  const getStatusColor = (remainingQuantity?: number, totalQuantity?: number) => {
    if (!remainingQuantity || !totalQuantity) return 'bg-gray-100 text-gray-800';
    
    if (remainingQuantity === 0) return 'bg-green-100 text-green-800';
    if (remainingQuantity < totalQuantity) return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusText = (remainingQuantity?: number, totalQuantity?: number) => {
    if (!remainingQuantity || !totalQuantity) return t('common.status.unknown', {}, 'Ukjent');
    
    if (remainingQuantity === 0) return t('warehouse.modules.receipt.receiptStatus.completelyReceived', {}, 'Komplett mottatt');
    if (remainingQuantity < totalQuantity) return t('warehouse.modules.receipt.receiptStatus.partiallyReceived', {}, 'Delvis mottatt');
    return t('warehouse.modules.receipt.receiptStatus.notReceived', {}, 'Ikke mottatt');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          {/* Header - Responsive */}
          <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 flex-1">
              <List className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mt-0.5 sm:mt-0 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                  <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.allOrderLines', {}, 'Alle ordrelinjer')} - </span>
                  {externalBatchId
                    ? t('warehouse.modules.receipt.orderHeader', { orderNo: externalBatchId }, `Ordre ${externalBatchId}`)
                    : t('warehouse.modules.receipt.orderHeader', { orderNo }, `Ordre ${orderNo}`)}
                </h2>
                <p className="text-gray-600 text-xs sm:text-sm mt-1">
                  {t('warehouse.modules.receipt.lineBrowser.selectOrderLine', {}, 'Velg en ordrelinje fra listen nedenfor')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Filter - Responsive */}
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('warehouse.modules.receipt.lineBrowser.filterPlaceholder', {}, 'Filtrer på varenummer, beskrivelse eller strekkode...')}
                className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Statistics - Responsive */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-green-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-green-600">
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.linesReceived', {}, 'Antall linjer')} </span>{t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-green-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      return totalReceived >= (line.quantity || 0) && (line.quantity || 0) > 0;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-yellow-600">
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.partiallyPrefix', {}, 'Delvis')} </span>{t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-yellow-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      const ordered = line.quantity || 0;
                      return totalReceived > 0 && totalReceived < ordered;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500 mb-1 sm:mb-0" />
                <div className="sm:ml-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">
                    <span className="hidden sm:inline">{t('warehouse.modules.receipt.lineBrowser.notPrefix', {}, 'Ikke')} </span>{t('warehouse.modules.receipt.fields.received', {}, 'mottatt')}
                  </p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">
                    {filteredLines.filter(line => {
                      const totalReceived = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
                      return totalReceived === 0;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Table View - Hidden on mobile/tablet */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hidden lg:block">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('warehouse.modules.receipt.orderLines', {}, 'Ordrelinjer')} ({filteredLines.length})
              </h3>
              {filteredLines.length > 0 && (
                <button
                  onClick={handleSelectAllLines}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  <Package className="h-4 w-4 mr-2" />
                  {t('warehouse.modules.receipt.lineBrowser.allLines', {}, 'Alle linjer')}
                </button>
              )}
            </div>
            
            {filteredLines.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">{t('warehouse.modules.receipt.lineBrowser.noLinesFound', {}, 'Ingen ordrelinjer funnet')}</p>
                <p className="text-sm">{t('warehouse.modules.receipt.lineBrowser.tryAdjustFilter', {}, 'Prøv å justere filteret eller kontroller ordrenummeret')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.general.itemNumber', {}, 'Varenummer')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.fields.description', {}, 'Beskrivelse')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                  {t('common.buttons.action', {}, 'Handling')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLines.map((line) => {
                      const bestilt = line.quantity || 0;
                      const mottatt = line.receivedQuantity || 0;
                      const ankommet = line.arrivedQuantity || 0;

                      // Bestem bakgrunnsfarge basert på totalt behandlet (mottatt + ankommet)
                      const totalBehandlet = mottatt + ankommet;
                      let rowBgColor = '';
                      if (totalBehandlet >= bestilt && bestilt > 0) {
                        rowBgColor = 'bg-green-50'; // Fullstendig mottatt
                      } else if (totalBehandlet > 0 && totalBehandlet < bestilt) {
                        rowBgColor = 'bg-yellow-50'; // Delvis mottatt
                      }

                      return (
                        <tr key={`${line.orderNo}-${line.lineNo}`} className={`hover:bg-gray-100 ${rowBgColor}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {line.itemNo}
                            </div>
                            <div className="text-xs text-gray-500">
                              {externalBatchId && line.orderNo
                                ? `${t('warehouse.modules.receipt.orderHeader', { orderNo: line.orderNo }, `Ordre ${line.orderNo}`)}, ${t('warehouse.modules.receipt.lineNumber', { lineNo: line.lineNo }, `Rad ${line.lineNo}`)}`
                                : t('warehouse.modules.receipt.lineNumber', { lineNo: line.lineNo }, `Rad ${line.lineNo}`)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {bestilt} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {mottatt} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {ankommet} {line.itemUnitTxt}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => onLineSelect(line)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                              <ArrowRight className="h-4 w-4 mr-1" />
                              {t('common.buttons.select', {}, 'Velg')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Mobile/Tablet Card View - Shown on small screens */}
          <div className="lg:hidden">
            <div className="mb-3 flex items-center justify-between px-1">
              <h3 className="text-base font-semibold text-gray-900">
                {t('warehouse.modules.receipt.orderLines', {}, 'Ordrelinjer')} ({filteredLines.length})
              </h3>
              {filteredLines.length > 0 && (
                <button
                  onClick={handleSelectAllLines}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                >
                  <Package className="h-3 w-3 mr-1" />
                  {t('warehouse.modules.receipt.lineBrowser.allLines', {}, 'Alle linjer')}
                </button>
              )}
            </div>

            {filteredLines.length === 0 ? (
              <div className="p-6 text-center text-gray-500 bg-white border border-gray-200 rounded-lg">
                <Package className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">{t('warehouse.modules.receipt.lineBrowser.noLinesFound', {}, 'Ingen ordrelinjer funnet')}</p>
                <p className="text-sm mt-1">{t('warehouse.modules.receipt.lineBrowser.tryAdjustFilterShort', {}, 'Prøv å justere filteret')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLines.map((line) => {
                  const bestilt = line.quantity || 0;
                  const mottatt = line.receivedQuantity || 0;
                  const ankommet = line.arrivedQuantity || 0;

                  // Bestem bakgrunnsfarge basert på totalt behandlet (mottatt + ankommet)
                  const totalBehandlet = mottatt + ankommet;
                  let bgColor = 'bg-white'; // Default - ikke mottatt
                  if (totalBehandlet >= bestilt && bestilt > 0) {
                    bgColor = 'bg-green-50'; // Fullstendig mottatt
                  } else if (totalBehandlet > 0 && totalBehandlet < bestilt) {
                    bgColor = 'bg-yellow-50'; // Delvis mottatt
                  }

                  return (
                    <div key={`${line.orderNo}-${line.lineNo}`} className={`${bgColor} border border-gray-200 rounded-lg shadow-sm p-3`}>
                      {/* Linje 1: Varenummer, Beskrivelse, Velg-knapp */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0 mr-2">
                          <div className="flex items-baseline space-x-2">
                            <span className="font-semibold text-gray-900 text-sm">
                              {line.itemNo}
                            </span>
                            <span className="text-xs text-gray-500">
                              - {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => onLineSelect(line)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex-shrink-0"
                        >
                          <ArrowRight className="h-3 w-3 mr-1" />
                          {t('common.buttons.select', {}, 'Velg')}
                        </button>
                      </div>

                      {/* Linje 2: Bestilt, Mottatt, Ankommet */}
                      <div className="flex items-center space-x-4 text-xs text-gray-700">
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}:</span> <span className="font-medium">{bestilt}</span>
                        </span>
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}:</span> <span className="font-medium">{mottatt}</span>
                        </span>
                        <span>
                          <span className="text-gray-500">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}:</span> <span className="font-medium">{ankommet}</span>
                        </span>
                        <span className="text-gray-500">{line.itemUnitTxt}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};